import { useEffect, useRef, useState } from "react";

import AuthorizationSection from "./components/Main/AuthorizationSection/AuthorizationSection";
import Header from "./components/Header/Header";
import CompaniesTable from "./components/Items/Tables/CompaniesTable";
// import ConfirmDeleteForm from "./components/Items/Forms/ConfirmDeleteForm";

import { languageSetterList } from "./components/Constants";

import { getInfo, updateCompanyInfo, updateUserInfo, createWebSocket, createUserByFileData } from "./api/api";

import "./App.scss";

const canonicalData = (companies) =>
  companies.map((company) => ({
    id: company.id,
    domain: company.domain,
    activationType: company.activationType,
    limit: company.limit,
    licenseExpiresIn: company.licenseExpiresIn,

    users: company.users.map((user) => ({
      id: user.id,
      activeAt: user.activeAt,
      companyName: user.companyName ?? "Company undefined",
      name: user.name,
      email: user.email,
      appVersion: user.appVersion,
      privilege: user.privilege,
      wsEnabled: user.wsEnabled ?? false,
      activationStatus: user.activationStatus,
      status: user.status,
      expireDate: user.expireDate,
    })),
  }));

function App() {
  const [language, setLanguage] = useState(() => {
    const savedLanguage = window.localStorage.getItem("ede-language");
    return savedLanguage === "en" ? "en" : savedLanguage === "ua" ? "ua" : "ru";
  });

  const [theme, setTheme] = useState(() => {
    const savedTheme = window.localStorage.getItem("ede-theme");
    return savedTheme === "dark" ? "dark" : "light";
  });

  const [isAuthorized, setIsAuthorized] = useState(() => {
    return Boolean(sessionStorage.getItem("token"));
  });

  const [canonical, setCanonical] = useState([]);
  // console.log("canonical :>> ", canonical);

  const userIsUpdatesRef = useRef("");
  const updatedCompanyIdRef = useRef("");

  const [isFormOpen, setFormOpenState] = useState(false);

  // const [deleteTarget, setDeleteTarget] = useState(null);
  // const [isDelete, setDeleteState] = useState(false);

  const handleAuthorizationSuccess = () => {
    setIsAuthorized(true);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("token");
    setIsAuthorized(false);
  };

  const setCompanyId = (companyID) => {
    updatedCompanyIdRef.current = companyID;
  };

  const setUserId = (userID) => {
    userIsUpdatesRef.current = userID;
  };

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    window.localStorage.setItem("ede-theme", theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.setAttribute("data-language", language);
    window.localStorage.setItem("ede-language", language);
  }, [language]);

  const fetchData = async () => {
    try {
      const result = await getInfo(sessionStorage.getItem("token"));

      setCanonical(canonicalData(result));

      return result;
    } catch (error) {
      console.error("Failed to get companies:", error);

      return null;
    }
  };

  // ---------------------------
  // WEBSOCKET
  // ---------------------------
  useEffect(() => {
    if (!isAuthorized) return;

    const token = sessionStorage.getItem("token");

    if (!token) return;

    fetchData();

    const socket = createWebSocket(token, (message) => {
      if (!message.code || !message.data) return;

      // console.log("FROM SERVER:", message);

      if (message.code === "init") {
        const webSocketInfo = message.data.domains;
        // console.log("webSocketInfo :>> ", webSocketInfo);

        const companyId = updatedCompanyIdRef.current;

        if (!companyId) {
          fetchData();
          return;
        }

        const updatedCompany = webSocketInfo.find((company) => company.id === companyId);

        // console.log("updatedCompany :>> ", updatedCompany);

        setCanonical((current) =>
          current.map((company) =>
            company.id === updatedCompany.id
              ? {
                  ...company,
                  activationType: updatedCompany.activationType,
                  licenseExpiresIn: updatedCompany.licenseExpiresIn,
                  limit: updatedCompany.limit,
                }
              : company,
          ),
        );
      }

      if (message.code === "licenses" || message.code === "licensesCheck") {
        fetchData();

        // const updatedUser = message.data.map((user) => user.payload);

        // console.log("updatedUser :>> ", updatedUser);
        // const userId = message.data.map((user) => user.id);

        // if (!userId) {
        //   fetchData();
        //   return;
        // }

        // setCanonical((current) =>
        //   current.map((company) => ({
        //     ...company,
        //     users: company.users.map((user) =>
        //       user.id === userId
        //         ? {
        //             ...user,
        //             privilege: updatedUser.privilege,
        //             wsEnabled: updatedUser.wsEnabled ?? false,
        //             activationStatus: updatedUser.activationStatus,
        //             status: updatedUser.status,
        //             expireDate: updatedUser.expireDate,
        //           }
        //         : user,
        //     ),
        //   })),
        // );
      }
    });

    return () => {
      socket.close();
    };
  }, [isAuthorized]);

  // смена состояния "Форма открыта | закрыта"
  const onAccessFormState = () => {
    setFormOpenState(!isFormOpen ? true : false);
  };

  // отправка в БД изменений по компании
  const updateCompany = async (company) => {
    const token = sessionStorage.getItem("token");

    try {
      await updateCompanyInfo(token, company);

      return true;
    } catch (error) {
      console.error("Company update error:", error);

      return false;
    }
  };

  // отправка в БД изменений по пользователю
  // кроме состояния Status
  const updateUser = async (user) => {
    const token = sessionStorage.getItem("token");

    try {
      await updateUserInfo(token, user);

      setCanonical((current) =>
        current.map((company) => ({
          ...company,
          users: company.users.map((item) =>
            item.id === user.id
              ? {
                  ...item,
                  ...user,
                }
              : item,
          ),
        })),
      );

      return true;
    } catch (error) {
      console.error("User update error:", error);

      return false;
    }
  };

  // // команда на удаление Компании
  // const deleteCompany = (companyId) => {
  //   const company = canonical.find((item) => item.id === companyId);

  //   if (!company) return;
  //   setDeleteState(true);

  //   setDeleteTarget({
  //     type: "company",
  //     companyId,
  //     name: `${company.domain}`,
  //     title: ` ${languageSetterList.deleteCompanyTitle[language]}`,
  //   });
  // };

  // // команда на удаление Пользователя
  // const deleteUser = (companyId, userId) => {
  //   const company = canonical.find((item) => item.id === companyId);
  //   const user = company?.users.find((item) => item.id === userId);

  //   if (!user) return;
  //   setDeleteState(true);

  //   setDeleteTarget({
  //     type: "user",
  //     companyId,
  //     userId,
  //     name: `${user.name}`,
  //     title: `${languageSetterList.deleteUserTitle[language]}`,
  //   });
  // };

  // // отмена Удения (окно просто закрывается)
  // const cancelDelete = () => {
  //   setDeleteState(false);

  //   setTimeout(() => {
  //     setDeleteTarget(null);
  //   }, 500);
  // };

  // // подтверждение Удения
  // // Тут уже решай сам, нужно ли будет удалять пользователя / компанию из БД, или просто архивировать
  // const confirmDelete = () => {
  //   if (!deleteTarget) return;

  //   if (deleteTarget.type === "company") {
  //     setCanonical((current) => current.filter((company) => company.id !== deleteTarget.companyId));
  //   }

  //   if (deleteTarget.type === "user") {
  //     debugger;
  //     setCanonical((current) =>
  //       current.map((company) =>
  //         company.id === deleteTarget.companyId
  //           ? {
  //               ...company,
  //               users: company.users.filter((user) => user.id !== deleteTarget.userId),
  //             }
  //           : company,
  //       ),
  //     );
  //   }

  //   cancelDelete();
  // };

  // Файл проверяется в AccessForm
  // Тут его нужно распарсить и создавать Пользователя и/или Компанию

  const createAccess = async (file) => {
    try {
      const token = sessionStorage.getItem("token");

      if (!token) return;

      await createUserByFileData(token, file);

      return true;
    } catch (error) {
      console.log("File parsing error :>> ", error);

      return false;
    }
  };

  return (
    <div className="app">
      {!isAuthorized ? (
        <AuthorizationSection language={language} onAuthorizationSuccess={handleAuthorizationSuccess} />
      ) : (
        <>
          <Header
            localization={languageSetterList}
            language={language}
            setLanguage={setLanguage}
            accessFormState={onAccessFormState}
            state={isFormOpen}
            theme={theme}
            setTheme={setTheme}
            createAccess={createAccess}
            onLogOut={handleLogout}
          />

          <main>
            <CompaniesTable
              localization={languageSetterList}
              language={language}
              companies={canonical}
              onUpdateCompany={updateCompany}
              // companyUpdated={companyUpdate}
              // onDeleteCompany={deleteCompany}
              getCompanyID={(companyID) => setCompanyId(companyID)}
              onUpdateUser={updateUser}
              // userUpdated={userUpdate}
              // onDeleteUser={deleteUser}
              getUserID={(userID) => setUserId(userID)}
            />

            {/* <ConfirmDeleteForm
              localization={languageSetterList}
              language={language}
              isOpen={isDelete}
              unitName={deleteTarget?.name || ""}
              title={deleteTarget?.title || ""}
              onConfirm={confirmDelete}
              onCancel={cancelDelete}
            /> */}
          </main>
        </>
      )}
    </div>
  );
}

export default App;
